import type { PaginationState, RowData } from '@tanstack/react-table'
import { useMemo } from 'react'

import { DataTable, type DataTableProps } from '@/components/data-table/DataTable'
import { useTablePreferences } from '@/hooks/use-table-preferences'

type ManagedProps =
  | 'pagination'
  | 'onPaginationChange'
  | 'sorting'
  | 'onSortingChange'
  | 'layout'
  | 'onLayoutChange'
  | 'onLayoutReset'
  | 'pageSizeOptions'
  | 'exportData'
  | 'fetchExportData'

export interface PagedDataTableProps<TData extends RowData> extends Omit<
  DataTableProps<TData>,
  ManagedProps
> {
  /** 1 tabanlı sayfa (admin sayfaları URL'de `page` tutar). */
  page: number
  pageSize: number
  onPageChange: (page: number) => void
}

const NO_VISIBILITY = {}
const NO_SORTING: never[] = []
const noop = () => undefined

/**
 * Sunucu sayfalamalı, sıralamasız liste tablosu (Kullanıcılar, Denetim kaydı, Sistem): `DataTable`'ın uiarc
 * grid görünümünü alır; sütun boyutu/sırası/gizleme kullanıcı tercihine yazılır, sayfa durumu çağıranda
 * (URL) kalır. Excel'e ekrandaki sayfa gider.
 */
export function PagedDataTable<TData extends RowData>({
  tableId,
  data,
  page,
  pageSize,
  onPageChange,
  ...props
}: PagedDataTableProps<TData>) {
  const preferences = useTablePreferences(tableId, NO_VISIBILITY)
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: Math.max(page - 1, 0), pageSize }),
    [page, pageSize],
  )
  const pageSizeOptions = useMemo(() => [pageSize], [pageSize])

  return (
    <DataTable<TData>
      {...props}
      tableId={tableId}
      data={data}
      exportData={data}
      pagination={pagination}
      onPaginationChange={(updater) => {
        const next = typeof updater === 'function' ? updater(pagination) : updater
        onPageChange(next.pageIndex + 1)
      }}
      pageSizeOptions={pageSizeOptions}
      sorting={NO_SORTING}
      onSortingChange={noop}
      layout={preferences.layout}
      onLayoutChange={preferences.save}
      onLayoutReset={preferences.reset}
    />
  )
}
